import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

function AdminUserView() {
    const { userId } = useParams();
    const [user, setUser] = useState(null);
    const [salaryData, setSalaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [detailModal, setDetailModal] = useState({ show: false, data: null, loading: false });

    useEffect(() => {
        fetchUserData();
        fetchSalaryData();
    }, [userId, selectedYear]);

    const fetchUserData = async () => {
        try {
            console.log('Fetching user profile for userId:', userId);
            const res = await api.get(`/admin/user-profile/${userId}`);
            console.log('User profile response:', res.data);
            setUser(res.data);
        } catch (err) {
            console.error('Error fetching user data:', err);
            console.error('Error details:', err.response?.data);
        } finally {
            setLoading(false);
        }
    };

    const fetchSalaryData = async () => {
        try {
            console.log('Fetching salary data for userId:', userId, 'year:', selectedYear);
            const res = await api.get(`/admin/user-salary/${userId}?year=${selectedYear}`);
            console.log('Salary data response:', res.data);
            setSalaryData(res.data.months || []);
            const years = res.data.availableYears || [];
            setAvailableYears(years);

            // ถ้ามีปีในระบบ และ selectedYear ไม่ใช่ปีที่มีข้อมูล ให้เปลี่ยนเป็นปีล่าสุด
            if (years.length > 0 && !years.includes(selectedYear)) {
                setSelectedYear(years[0]);
            }
        } catch (err) {
            console.error('Error fetching salary data:', err);
            console.error('Error details:', err.response?.data);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return parseFloat(amount).toLocaleString('th-TH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getMonthName = (month) => {
        const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
                       'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
        return months[month - 1] || '';
    };

    const handleViewSlip = async (yyyy, mm) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/report/admin/slip/${user.cid}/${yyyy}/${mm}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                // ลอง parse เป็น JSON ถ้าไม่ได้ให้แสดง status text
                try {
                    const error = await response.json();
                    alert(error.error || `ไม่สามารถโหลดสลิปได้ (${response.status})`);
                } catch {
                    alert(`ไม่สามารถโหลดสลิปได้: ${response.statusText || response.status}`);
                }
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error viewing slip:', err);
            alert('เกิดข้อผิดพลาดในการโหลดสลิป: ' + err.message);
        }
    };

    const handleView50Tawi = async (yyyy) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/report/admin/50-tawi/${user.cid}/${yyyy}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                try {
                    const error = await response.json();
                    alert(error.error || `ไม่สามารถโหลด 50 ทวิได้ (${response.status})`);
                } catch {
                    alert(`ไม่สามารถโหลด 50 ทวิได้: ${response.statusText || response.status}`);
                }
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error viewing 50 tawi:', err);
            alert('เกิดข้อผิดพลาดในการโหลด 50 ทวิ: ' + err.message);
        }
    };

    // ดูรายละเอียดเงินเดือน
    const handleViewDetail = async (yyyy, mm) => {
        try {
            setDetailModal({ show: true, data: null, loading: true });
            const response = await api.get(`/admin/user-salary-detail/${userId}/${yyyy}/${mm}`);
            setDetailModal({ show: true, data: response.data, loading: false });
        } catch (err) {
            console.error('Error fetching salary detail:', err);
            alert('ไม่สามารถโหลดรายละเอียดได้: ' + (err.response?.data?.error || err.message));
            setDetailModal({ show: false, data: null, loading: false });
        }
    };

    const closeDetailModal = () => {
        setDetailModal({ show: false, data: null, loading: false });
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="admin-page">
                <div className="steam-card">
                    <div className="steam-card-body text-center">
                        <p>ไม่พบข้อมูลผู้ใช้</p>
                        <Link to="/admin" className="steam-btn steam-btn-primary" style={{ marginTop: '16px' }}>
                            กลับหน้าแรก
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>ดูข้อมูลผู้ใช้</span>
            </div>

            <div className="admin-header">
                <h1>ข้อมูลผู้ใช้</h1>
                <p>มุมมองข้อมูลส่วนตัวของผู้ใช้</p>
            </div>

            {/* User Profile Card */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ข้อมูลส่วนตัว</div>
                <div className="steam-card-body">
                    <div className="user-view-avatar-row">
                        <div className={`user-view-avatar ${user.avatar_url ? 'has-image' : ''}`}>
                            {user.avatar_url ? (
                                <img
                                    src={`/api${user.avatar_url}`}
                                    alt={user.fullname || 'avatar'}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentNode.classList.remove('has-image');
                                        e.target.parentNode.textContent = user.fullname?.charAt(0) || '?';
                                    }}
                                />
                            ) : (
                                user.fullname?.charAt(0) || '?'
                            )}
                        </div>
                        <div className="user-view-avatar-name">
                            <div className="uv-name">{user.fullname || '-'}</div>
                            {user.position && <div className="uv-pos">{user.position}</div>}
                        </div>
                    </div>
                    <div className="user-profile-grid">
                        <div className="profile-item">
                            <label>ชื่อ-นามสกุล</label>
                            <div className="profile-value">{user.fullname || '-'}</div>
                        </div>
                        <div className="profile-item">
                            <label>เลขบัตรประชาชน</label>
                            <div className="profile-value">{user.cid || '-'}</div>
                        </div>
                        <div className="profile-item">
                            <label>รหัสพนักงาน</label>
                            <div className="profile-value">{user.emp_code || '-'}</div>
                        </div>
                        <div className="profile-item">
                            <label>ตำแหน่ง</label>
                            <div className="profile-value">{user.position || '-'}</div>
                        </div>
                        <div className="profile-item">
                            <label>หน่วยงาน</label>
                            <div className="profile-value">{user.department || '-'}</div>
                        </div>
                        <div className="profile-item">
                            <label>สถานะ</label>
                            <div className="profile-value">
                                <span className={`badge ${user.is_active ? 'success' : 'danger'}`}>
                                    {user.is_active ? 'ใช้งาน' : 'ระงับ'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Salary Data */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>ประวัติเงินเดือน</span>
                        {availableYears.length > 0 && (
                            <select
                                className="steam-input"
                                style={{ width: 'auto', minWidth: '120px' }}
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            >
                                {availableYears.map(year => (
                                    <option key={year} value={year}>
                                        พ.ศ. {year + 543}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
                <div className="steam-card-body">
                    {availableYears.length === 0 ? (
                        <div className="text-center text-muted" style={{ padding: '40px 20px' }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="48" height="48" style={{ margin: '0 auto 16px', opacity: 0.3 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p>ผู้ใช้รายนี้ยังไม่มีข้อมูลเงินเดือนในระบบ</p>
                        </div>
                    ) : salaryData.length === 0 ? (
                        <div className="text-center text-muted" style={{ padding: '40px 20px' }}>
                            ไม่พบข้อมูลเงินเดือนในปี {selectedYear + 543}
                        </div>
                    ) : (
                        <div className="salary-grid">
                            {salaryData.map(month => (
                                <div key={month.month} className="salary-month-card">
                                    <div className="salary-month-header">
                                        <div className="month-name">
                                            {getMonthName(month.month)} {selectedYear + 543}
                                        </div>
                                        <div className="month-amount">
                                            ฿{formatCurrency(month.net_pay)}
                                        </div>
                                    </div>
                                    <div className="salary-month-details">
                                        <div className="detail-row">
                                            <span className="detail-label">รายได้</span>
                                            <span className="detail-value income">
                                                +฿{formatCurrency(month.total_income)}
                                            </span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">รายการหัก</span>
                                            <span className="detail-value deduction">
                                                -฿{formatCurrency(month.total_deduction)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="salary-month-actions">
                                        <button
                                            className="steam-btn steam-btn-sm steam-btn-primary"
                                            onClick={() => handleViewDetail(selectedYear, month.month)}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            รายละเอียด
                                        </button>
                                        <button
                                            className="steam-btn steam-btn-sm steam-btn-secondary"
                                            onClick={() => handleViewSlip(selectedYear, month.month)}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            ดูสลิป
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 50 Tawi Certificate */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">หนังสือรับรองภาษี (50 ทวิ)</div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        ดูหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ของผู้ใช้รายนี้
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {availableYears.map(year => (
                            <button
                                key={year}
                                className="steam-btn steam-btn-secondary"
                                onClick={() => handleView50Tawi(year)}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px' }}>
                                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                </svg>
                                ปี {year + 543}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Salary Detail Modal */}
            {detailModal.show && (
                <div className="modal-overlay" onClick={closeDetailModal}>
                    <div className="modal-content salary-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>รายละเอียดเงินเดือน</h3>
                            <button className="modal-close" onClick={closeDetailModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {detailModal.loading ? (
                                <div className="text-center" style={{ padding: '40px' }}>
                                    <div className="spinner"></div>
                                    <p style={{ marginTop: '16px' }}>กำลังโหลด...</p>
                                </div>
                            ) : detailModal.data ? (
                                <>
                                    <div className="salary-summary">
                                        <div className="summary-item">
                                            <span className="summary-label">เดือน/ปี</span>
                                            <span className="summary-value">
                                                {getMonthName(detailModal.data.header?.mm)} {(detailModal.data.header?.yyyy || 0) + 543}
                                            </span>
                                        </div>
                                        <div className="summary-item">
                                            <span className="summary-label">รายรับสุทธิ</span>
                                            <span className="summary-value highlight">
                                                ฿{formatCurrency(detailModal.data.header?.cnet || detailModal.data.header?.tr_net || 0)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="salary-detail-grid">
                                        <div className="detail-section">
                                            <h4 className="section-title income">รายได้</h4>
                                            <div className="detail-list">
                                                {detailModal.data.income?.map((item, idx) => (
                                                    <div key={idx} className="detail-item">
                                                        <span className="item-name">{item.acc_name || `รหัส ${item.code}`}</span>
                                                        <span className="item-amount income">+฿{formatCurrency(item.amt)}</span>
                                                    </div>
                                                ))}
                                                {(!detailModal.data.income || detailModal.data.income.length === 0) && (
                                                    <div className="no-items">ไม่มีรายการ</div>
                                                )}
                                                <div className="detail-item total">
                                                    <span className="item-name">รวมรายได้</span>
                                                    <span className="item-amount income">
                                                        +฿{formatCurrency(detailModal.data.income?.reduce((sum, i) => sum + (i.amt || 0), 0) || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="detail-section">
                                            <h4 className="section-title deduction">รายการหัก</h4>
                                            <div className="detail-list">
                                                {detailModal.data.deduction?.map((item, idx) => (
                                                    <div key={idx} className="detail-item">
                                                        <span className="item-name">{item.acc_name || `รหัส ${item.code}`}</span>
                                                        <span className="item-amount deduction">-฿{formatCurrency(Math.abs(item.amt))}</span>
                                                    </div>
                                                ))}
                                                {(!detailModal.data.deduction || detailModal.data.deduction.length === 0) && (
                                                    <div className="no-items">ไม่มีรายการ</div>
                                                )}
                                                <div className="detail-item total">
                                                    <span className="item-name">รวมรายการหัก</span>
                                                    <span className="item-amount deduction">
                                                        -฿{formatCurrency(detailModal.data.deduction?.reduce((sum, i) => sum + Math.abs(i.amt || 0), 0) || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center" style={{ padding: '40px' }}>
                                    <p>ไม่พบข้อมูล</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminUserView;
